<template>
    <div class="c-up" :style="wrapStyle">
        <div :style="innerStyle">
            <div :style="headerStyle">
                <div :style="eyebrowStyle">{{ (currentBrand || agencyName || '').toUpperCase() }}</div>
                <h1 :style="h1Style">Tell us about the new upsell.</h1>
                <p :style="leadStyle">
                    Provide detailed information about a service in this SOW. Pick a
                    service, choose how it's structured, and (if you've already billed
                    for it) record the invoice.
                </p>
            </div>

            <div :style="bodyGridStyle">
                <!-- ===== Main column ===== -->
                <div :style="mainColStyle">
                    <!-- Card 1: Service identity -->
                    <CCard :extra-style="cardStyle">
                        <div :style="cardGridStyle">
                            <div>
                                <label :style="fieldLabelStyle">
                                    Service <span :style="requiredMark">*</span>
                                </label>
                                <div v-if="is_create_custom_service" :style="inputWrapStyle">
                                    <input
                                        type="text"
                                        id="custom_service"
                                        v-model="upsell.custom_service"
                                        name="custom_service"
                                        placeholder="Name your custom service"
                                        :style="bareInputStyle"
                                    />
                                </div>
                                <div v-else class="form-field form-field--modal form-field--tree">
                                    <treeselect
                                        v-model="upsell.services"
                                        id="service_id"
                                        name="service_id"
                                        :multiple="true"
                                        :valueConsistsOf="'LEAF_PRIORITY'"
                                        :options="services_list"
                                    />
                                </div>
                                <span v-if="errors.custom_service" :style="fieldErrorStyle">{{ errors.custom_service }}</span>
                            </div>
                            <div>
                                <label :style="fieldLabelStyle">
                                    Kick-off date <span :style="optionalBadge">optional</span>
                                </label>
                                <div
                                    class="form-field form-field--datepicker"
                                    ref="datepicker"
                                    :style="datepickerWrapStyle"
                                >
                                    <input
                                        type="text"
                                        id="kickoff_date"
                                        v-model="upsell.kick_off_date"
                                        name="kickoff_date"
                                        placeholder="mm/dd/yyyy"
                                        :style="datepickerInputStyle"
                                        @input="onChangeDatePicker"
                                        @changeDate="onChangeDatePicker"
                                    />
                                    <button type="button" class="btn btn--icon" :style="datepickerBtnStyle">
                                        <i class="icon icon-calendar"></i>
                                    </button>
                                </div>
                                <span v-if="errors.kick_off_date" :style="fieldErrorStyle">{{ errors.kick_off_date }}</span>
                            </div>
                        </div>

                        <label :style="customToggleStyle">
                            <input
                                type="checkbox"
                                id="is_create_custom_service"
                                name="is_create_custom_service"
                                :checked="is_create_custom_service"
                                :style="srOnly"
                                @change="is_create_custom_service = !is_create_custom_service"
                            />
                            <span :style="customToggleBoxStyle(is_create_custom_service)">
                                <span v-if="is_create_custom_service">✓</span>
                            </span>
                            <span :style="customToggleLabelStyle">Create a custom service instead</span>
                        </label>

                        <div :style="sowChipStyle">
                            <span :style="sowChipBadgeStyle">SOW</span>
                            <span :style="sowChipDividerStyle" />
                            <span :style="sowChipNameStyle">{{ upsell.sow_title || '—' }}</span>
                            <span :style="sowChipNoteStyle">linked automatically</span>
                        </div>
                    </CCard>

                    <!-- Card 2: Type with expandable rows -->
                    <CCard :extra-style="cardStyle">
                        <div :style="cardSectionLabelStyle">
                            Type <span :style="requiredMark">*</span>
                        </div>
                        <div :style="typeRowsStyle">
                            <div
                                v-for="t in brand_types"
                                :key="`type_${t.id}`"
                                :style="typeRowStyle(upsell.type === t.id)"
                            >
                                <label
                                    :for="`type_${t.id}`"
                                    :style="typeRowHeadStyle"
                                >
                                    <input
                                        type="radio"
                                        :id="`type_${t.id}`"
                                        v-model="upsell.type"
                                        :checked="upsell.type === t.id"
                                        name="type_id"
                                        class="type-create-deals js-edit-deal-type"
                                        :value="t.id"
                                        :style="srOnly"
                                        @change="onChangeType"
                                    />
                                    <span :style="cRadioDotStyle(upsell.type === t.id)">
                                        <span v-if="upsell.type === t.id" :style="cRadioInnerStyle" />
                                    </span>
                                    <div :style="typeRowBodyStyle">
                                        <div :style="typeRowTitleStyle">{{ t.name }}</div>
                                        <div :style="typeRowSubStyle">{{ typeRowSub(t.id) }}</div>
                                    </div>
                                </label>

                                <!-- Retainer expansion -->
                                <div v-if="upsell.type === 1 && t.id === 1" :style="typeRowExpandStyle">
                                    <div :style="typeRowExpandGridStyle">
                                        <div>
                                            <label :style="fieldLabelStyle">Monthly fee</label>
                                            <CurrencyInput
                                                :id="`amount_field_${t.id}`"
                                                :ref="`amount_field_${t.id}`"
                                                :disabled="upsell.is_fluctuating_fee === true"
                                                :name="`amount_field_${t.id}`"
                                                v-model="upsell[get_deal_service_type(t.id)]"
                                                @change="handleChangeAmount"
                                                :options="currencyInputOpts"
                                            />
                                        </div>
                                        <div>
                                            <label :style="fieldLabelStyle">Fee mode</label>
                                            <div :style="feeModeGridStyle">
                                                <label :for="`is_fluctuating_fee_fixed`" :style="cRadioStyle(upsell.is_fluctuating_fee === false)">
                                                    <input
                                                        type="radio"
                                                        :checked="upsell.is_fluctuating_fee === false"
                                                        id="is_fluctuating_fee_fixed"
                                                        name="is_fluctuating_fee"
                                                        :style="srOnly"
                                                        @change="() => onChangeFluctuatingFee(false)"
                                                    />
                                                    <span :style="cRadioDotStyle(upsell.is_fluctuating_fee === false)">
                                                        <span v-if="upsell.is_fluctuating_fee === false" :style="cRadioInnerStyle" />
                                                    </span>
                                                    <span :style="feeModeLabelStyle">Fixed</span>
                                                </label>
                                                <label :for="`is_fluctuating_fee_true`" :style="cRadioStyle(upsell.is_fluctuating_fee === true)">
                                                    <input
                                                        type="radio"
                                                        :checked="upsell.is_fluctuating_fee === true"
                                                        id="is_fluctuating_fee_true"
                                                        name="is_fluctuating_fee"
                                                        :style="srOnly"
                                                        @change="() => onChangeFluctuatingFee(true)"
                                                    />
                                                    <span :style="cRadioDotStyle(upsell.is_fluctuating_fee === true)">
                                                        <span v-if="upsell.is_fluctuating_fee === true" :style="cRadioInnerStyle" />
                                                    </span>
                                                    <span :style="feeModeLabelStyle">Fluctuating</span>
                                                </label>
                                            </div>
                                        </div>
                                        <div :style="frequencyWrapStyle">
                                            <label :style="fieldLabelStyle">Frequency</label>
                                            <div class="form-field form-field--tree">
                                                <treeselect
                                                    v-model="upsell.frequency"
                                                    id="frequency"
                                                    name="frequency"
                                                    :clearable="false"
                                                    :multiple="false"
                                                    :options="frequency_list"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <span v-if="errors.is_fluctuating_fee" :style="fieldErrorStyle">{{ errors.is_fluctuating_fee }}</span>
                                    <span v-if="errors.amount" :style="fieldErrorStyle">{{ errors.amount }}</span>
                                </div>

                                <!-- Project expansion -->
                                <div v-if="upsell.type === 2 && t.id === 2" :style="typeRowExpandStyle">
                                    <div :style="typeRowExpandGrid2Style">
                                        <div>
                                            <label :style="fieldLabelStyle">Full amount</label>
                                            <CurrencyInput
                                                :id="`amount_field_${t.id}`"
                                                :ref="`amount_field_${t.id}`"
                                                :name="`amount_field_${t.id}`"
                                                v-model="upsell[get_deal_service_type(t.id)]"
                                                @change="handleChangeAmount"
                                                :options="currencyInputOpts"
                                            />
                                        </div>
                                        <div>
                                            <label :style="fieldLabelStyle">Number of payments</label>
                                            <div :style="inputWrapStyle">
                                                <input
                                                    type="number"
                                                    id="number_of_pmts"
                                                    v-model="upsell.number_of_pmts"
                                                    name="number_of_pmts"
                                                    placeholder="e.g. 3"
                                                    :style="bareInputStyle"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <span v-if="errors.amount" :style="fieldErrorStyle">{{ errors.amount }}</span>
                                    <span v-if="errors.number_of_pmts" :style="fieldErrorStyle">{{ errors.number_of_pmts }}</span>
                                </div>

                                <!-- One-time expansion -->
                                <div v-if="upsell.type === 3 && t.id === 3" :style="typeRowExpandStyle">
                                    <div :style="typeRowExpandOneStyle">
                                        <label :style="fieldLabelStyle">One-time fee</label>
                                        <CurrencyInput
                                            :id="`amount_field_${t.id}`"
                                            :ref="`amount_field_${t.id}`"
                                            :name="`amount_field_${t.id}`"
                                            v-model="upsell[get_deal_service_type(t.id)]"
                                            @change="handleChangeAmount"
                                            :options="currencyInputOpts"
                                        />
                                    </div>
                                    <span v-if="errors.amount" :style="fieldErrorStyle">{{ errors.amount }}</span>
                                </div>
                            </div>
                        </div>
                    </CCard>

                    <!-- Card 3: Already invoiced toggle + fields + Terms -->
                    <CCard :extra-style="cardStyle">
                        <div :style="invoicedHeaderStyle">
                            <label :style="invoicedToggleStyle">
                                <input
                                    type="checkbox"
                                    id="same_as_hq"
                                    class="one-time-setup"
                                    name="is_has_invoice"
                                    v-model="isHasInvoice"
                                    :style="srOnly"
                                    @change="(e) => handleChangeInoviced(e.target.checked)"
                                />
                                <span :style="customToggleBoxStyle(isHasInvoice)">
                                    <span v-if="isHasInvoice">✓</span>
                                </span>
                                <span>
                                    <span :style="invoicedTitleStyle">Already invoiced</span>
                                    <span :style="invoicedPeriodStyle">({{ invoicedPeriod }})</span>
                                </span>
                            </label>
                            <span v-if="isHasInvoice" :style="invoicedActiveBadgeStyle">● recording invoice</span>
                        </div>

                        <div v-if="isHasInvoice" :style="invoiceGridStyle">
                            <div>
                                <label :style="fieldLabelStyle">Invoice #</label>
                                <div :style="inputWrapStyle">
                                    <input
                                        v-model="newInvoice.invoice_number"
                                        id="invoice_number"
                                        name="invoice_number"
                                        placeholder="—"
                                        :style="bareInputStyle"
                                    />
                                </div>
                            </div>
                            <div>
                                <label :style="fieldLabelStyle">Amount</label>
                                <CurrencyInput
                                    :options="currencyInputOpts"
                                    v-model="newInvoice.amount"
                                />
                            </div>
                            <div>
                                <label :style="fieldLabelStyle">Collected</label>
                                <CurrencyInput
                                    :options="currencyInputOpts"
                                    v-model="newInvoice.collected"
                                />
                            </div>
                            <div>
                                <label :style="fieldLabelStyle">Hard cost</label>
                                <CurrencyInput
                                    :options="currencyInputOpts"
                                    v-model="newInvoice.hard_cost"
                                />
                            </div>
                        </div>

                        <div :style="termsWrapStyle">
                            <label :style="fieldLabelStyle">
                                Terms <span :style="optionalBadge">optional</span>
                            </label>
                            <textarea
                                id="terms"
                                v-model="upsell.terms"
                                name="terms"
                                placeholder="Anything specific to this engagement — scope notes, deliverable dates, payment terms…"
                                :style="textareaStyle"
                            ></textarea>
                        </div>
                    </CCard>
                </div>

                <!-- ===== Right rail: Live preview ===== -->
                <div :style="rightColStyle">
                    <CCard :extra-style="previewCardStyle">
                        <div :style="previewLabelStyle">Live preview</div>
                        <div :style="previewTitleStyle">
                            {{ previewServiceName || 'Untitled service' }}
                        </div>
                        <div :style="previewSubStyle">for {{ currentBrand || agencyName }}</div>
                        <div :style="previewKvBlockStyle">
                            <div :style="previewKvRowStyle">
                                <span :style="previewKvKStyle">Type</span>
                                <span :style="previewKvVStyle">{{ previewTypeLabel }}</span>
                            </div>
                            <template v-if="upsell.type === 1">
                                <div :style="previewKvRowStyle">
                                    <span :style="previewKvKStyle">Monthly</span>
                                    <span :style="previewKvVStyle">
                                        ${{ upsell.mounthly_fee || '0' }} ·
                                        {{ upsell.is_fluctuating_fee ? 'Fluctuating' : 'Fixed' }}
                                    </span>
                                </div>
                                <div :style="previewKvRowStyle">
                                    <span :style="previewKvKStyle">Cadence</span>
                                    <span :style="previewKvVStyle">{{ frequencyLabel }}</span>
                                </div>
                            </template>
                            <template v-if="upsell.type === 2">
                                <div :style="previewKvRowStyle">
                                    <span :style="previewKvKStyle">Total</span>
                                    <span :style="previewKvVStyle">${{ upsell.full_amount || '0' }}</span>
                                </div>
                                <div :style="previewKvRowStyle">
                                    <span :style="previewKvKStyle">Payments</span>
                                    <span :style="previewKvVStyle">{{ upsell.number_of_pmts || '—' }}</span>
                                </div>
                            </template>
                            <template v-if="upsell.type === 3">
                                <div :style="previewKvRowStyle">
                                    <span :style="previewKvKStyle">Fee</span>
                                    <span :style="previewKvVStyle">${{ upsell.one_time_fee || '0' }}</span>
                                </div>
                            </template>
                            <div :style="previewKvRowStyle">
                                <span :style="previewKvKStyle">Kick-off</span>
                                <span :style="previewKvVStyle">{{ upsell.kick_off_date || '—' }}</span>
                            </div>
                        </div>
                        <div v-if="isHasInvoice && hasInvoiceAmounts" :style="previewInvoiceCardStyle">
                            <div :style="previewInvoiceLabelStyle">Invoice this period</div>
                            <div :style="previewInvoiceValueStyle">
                                ${{ Number(newInvoice.amount || 0).toFixed(2) }}
                            </div>
                            <div :style="previewInvoiceBreakdownStyle">
                                ${{ Number(newInvoice.collected || 0).toFixed(2) }} collected ·
                                ${{ Number(newInvoice.hard_cost || 0).toFixed(2) }} hard cost
                            </div>
                        </div>
                    </CCard>
                    <div :style="rightHintStyle">
                        <span :style="rightHintIconStyle">i</span>
                        <span>Anything you fill in here can still be edited from the report screen at the end.</span>
                    </div>
                </div>
            </div>

            <div :style="navStyle">
                <CBtn ghost @click="onPrevSection">← Previous</CBtn>
                <span :style="navHintStyle">autosaved as you go</span>
                <CBtn primary big :disabled="isLoading" @click="onSaveNewUpsell">Next →</CBtn>
            </div>

            <div class="loader" v-if="isLoading" :style="loaderStyle">
                <clip-loader :loading="isLoading" color="#ec4657" size="45px" />
            </div>
        </div>
    </div>
</template>

<script>
import ClipLoader from "vue-spinner/src/ClipLoader";

import CurrencyInput from "../../../components/CurrencyInput";
import { Datepicker } from "vanillajs-datepicker";
import Treeselect from "@riophae/vue-treeselect";
import "@riophae/vue-treeselect/dist/vue-treeselect.css";
import { SECTIONS } from "../consts";
import axios from "axios";
import { reInitAllTooltips } from "../../../helpers/_tooltip";
import moment from "moment";
import { scrollToTop } from "../../../helpers/_scrollToTop";

import CCard from "../c-design/CCard.vue";
import CBtn from "../c-design/CBtn.vue";
import viewportMixin from "../c-design/useViewport";
import {
    C_BG,
    C_INK,
    C_INK2,
    C_MUTED,
    C_HAIR,
    C_CREAM,
    C_GREEN,
    C_RED,
    FONT_DISPLAY,
    FONT_BODY,
    navMobileStickyStyle,
} from "../c-design/tokens";

export default {
    name: "Wizard_Upsell",
    mixins: [viewportMixin],
    props: ["onChangeSection"],
    data() {
        return {
            SECTIONS,
            isLoading: false,
            services_list: [],
            frequency_list: [
                {
                    id: 1,
                    label: "1m",
                },
                {
                    id: 12,
                    label: "1y",
                },
            ],
            brand_types: [
                {
                    id: 1,
                    name: "Retainer",
                },
                {
                    id: 2,
                    name: "Project",
                },
                {
                    id: 3,
                    name: "One-Time Fee",
                },
            ],
            upsell: {
                services: [],
                kick_off_date: null,
                custom_service: "",
                terms: "",
                sow_title: "",
                type: 1,
                one_time_fee: "",
                mounthly_fee: "",
                full_amount: "",
                number_of_pmts: "",
                frequency: 1,
                is_fluctuating_fee: null,
            },
            datepicker_options: {
                format: "mm/dd/yyyy",
                pickLevel: 0,
                autohide: true,
            },
            wizard_draft_sow_id: null,
            errors: {
                service_id: null,
                type: null,
                amount: null,
                number_of_pmts: null,
                kick_off_date: null,
                sow_file: null,
                custom_service: null,
                is_fluctuating_fee: null,
            },
            urlRegex:
                /^https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_\+.~#?&\/=]*)$/,

            isHasInvoice: false,
            newInvoice: {
                amount: 0,
                collected: 0,
                hard_cost: 0,
                invoice_number: "",
            },
            is_create_custom_service: false,
        };
    },
    components: {
        Treeselect,
        ClipLoader,
        CurrencyInput,
        CCard,
        CBtn,
    },
    mounted() {
        this.initDatePicker();

        this.getData();

        const is_has_invoice = this.$store.getters.getIsWithoutSOW;

        if (is_has_invoice) {
            this.isHasInvoice = is_has_invoice;
        }

        setTimeout(() => {
            reInitAllTooltips();
        }, 750);

        scrollToTop();
    },
    computed: {
        prevSection() {
            return this.$store.getters.getPrevComponent;
        },
        getDealId() {
            return (
                this.$store.getters.getSalesPages(
                    this.$store.getters.getDealSalesIndex
                ).deal_id || null
            );
        },
        agencyName() {
            return this.$store.getters.getAgencyName;
        },
        currentBrand() {
            return this.$store.getters.getCurrentBrandName;
        },
        sowIndex() {
            return this.$store.getters.getCurrentIndexSOW;
        },
        upsellIndex() {
            return this.$store.getters.getCurrentIndexUpsell;
        },
        isEditUpsell() {
            return this.$store.getters.getIsEditUpsell;
        },
        invoicedPeriod() {
            return (
                moment().subtract(1, "months").endOf("month").format("MMMM") ||
                ""
            );
        },
        // ===== Live-preview helpers =====
        currencyInputOpts() {
            return {
                currency: 'USD',
                hideCurrencySymbolOnFocus: false,
                hideGroupingSeparatorOnFocus: false,
                hideNegligibleDecimalDigitsOnFocus: false,
            };
        },
        previewTypeLabel() {
            const t = (this.brand_types || []).find((b) => b.id === this.upsell.type);
            return t ? t.name : '—';
        },
        frequencyLabel() {
            const f = (this.frequency_list || []).find((x) => x.id === this.upsell.frequency);
            return f ? f.label : '—';
        },
        previewServiceName() {
            if (this.is_create_custom_service) return this.upsell.custom_service;
            // Treeselect stores leaf IDs in upsell.services. The services_list is
            // a tree shape; pick the first selected leaf for the preview.
            if (!Array.isArray(this.upsell.services) || this.upsell.services.length === 0) {
                return '';
            }
            const firstId = this.upsell.services[0];
            const walk = (nodes) => {
                if (!Array.isArray(nodes)) return null;
                for (const n of nodes) {
                    if (n && n.id === firstId) return n.label || n.name || '';
                    if (n && n.children) {
                        const hit = walk(n.children);
                        if (hit) return hit;
                    }
                }
                return null;
            };
            return walk(this.services_list) || '';
        },
        hasInvoiceAmounts() {
            return parseFloat(this.newInvoice.amount) > 0
                || parseFloat(this.newInvoice.collected) > 0;
        },
        // ===== c-design style maps =====
        wrapStyle() {
            return {
                background: C_BG,
                minHeight: '70vh',
                padding: this.isMobile ? '14px 12px 110px' : '24px 16px 40px',
            };
        },
        innerStyle() {
            return { maxWidth: '1180px', margin: '0 auto' };
        },
        bodyGridStyle() {
            // Mobile: collapse the right rail beneath the main column so the
            // live preview falls below the form. Desktop keeps the 300px
            // sticky rail.
            return {
                display: 'grid',
                gridTemplateColumns: this.isMobile ? '1fr' : 'minmax(0, 1fr) 300px',
                gap: this.isMobile ? '12px' : '20px',
                alignItems: 'start',
            };
        },
        mainColStyle() {
            return {
                display: 'flex',
                flexDirection: 'column',
                gap: this.isMobile ? '12px' : '16px',
            };
        },
        rightColStyle() {
            // Mobile: drop the sticky behaviour — it'd hide most of the form
            // on small screens.
            return this.isMobile
                ? { display: 'flex', flexDirection: 'column', gap: '12px' }
                : { position: 'sticky', top: '24px', display: 'flex', flexDirection: 'column', gap: '12px' };
        },
        cardGridStyle() {
            return {
                display: 'grid',
                gridTemplateColumns: this.isMobile ? '1fr' : '1fr 220px',
                gap: this.isMobile ? '14px' : '18px',
            };
        },
        fieldLabelStyle() {
            return {
                display: 'flex', alignItems: 'baseline', gap: '8px',
                marginBottom: '7px', fontSize: '12px', color: C_INK2,
                fontWeight: 600, letterSpacing: '0.1px',
            };
        },
        requiredMark() {
            return { color: C_RED, marginLeft: '3px' };
        },
        optionalBadge() {
            return { fontSize: '11px', color: C_MUTED, fontWeight: 400 };
        },
        inputWrapStyle() {
            return {
                display: 'flex', alignItems: 'center',
                border: `1.5px solid ${C_HAIR}`, borderRadius: '14px',
                background: C_CREAM, padding: '0 14px',
            };
        },
        bareInputStyle() {
            return {
                flex: 1, border: 'none', outline: 'none',
                background: 'transparent', padding: '14px 0',
                fontSize: '16px', fontFamily: FONT_BODY,
                fontWeight: 500, color: C_INK, width: '100%',
            };
        },
        datepickerWrapStyle() {
            return {
                display: 'flex', alignItems: 'center',
                border: `1.5px solid ${C_HAIR}`, borderRadius: '14px',
                background: C_CREAM, padding: '0 12px',
            };
        },
        datepickerInputStyle() {
            return {
                flex: 1, border: 'none', outline: 'none',
                background: 'transparent', padding: '14px 0',
                fontSize: '14px', fontFamily: FONT_BODY,
                color: C_INK, width: '100%',
            };
        },
        datepickerBtnStyle() {
            return {
                background: 'transparent', border: 'none',
                padding: '6px 4px', margin: '0 0 0 4px',
                fontSize: '16px', color: C_MUTED, cursor: 'pointer',
            };
        },
        srOnly() {
            return {
                position: 'absolute', width: '1px', height: '1px',
                margin: '-1px', overflow: 'hidden',
                clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: '0',
            };
        },
        customToggleStyle() {
            return {
                marginTop: '14px',
                display: 'flex', alignItems: 'center', gap: '10px',
                cursor: 'pointer', fontSize: '13px',
            };
        },
        customToggleLabelStyle() {
            return { fontSize: '13px', color: C_INK2, fontWeight: 500 };
        },
        sowChipStyle() {
            return {
                marginTop: '16px', padding: '12px 14px',
                background: C_CREAM, borderRadius: '10px',
                display: 'flex', alignItems: 'center', gap: '10px',
                fontSize: '12px', color: C_MUTED,
            };
        },
        sowChipBadgeStyle() {
            return {
                fontSize: '11px', letterSpacing: '0.5px',
                textTransform: 'uppercase', fontWeight: 700, color: C_INK2,
            };
        },
        sowChipDividerStyle() {
            return { width: '1px', height: '14px', background: C_HAIR, display: 'inline-block' };
        },
        sowChipNameStyle() {
            return { color: C_INK, fontWeight: 500 };
        },
        sowChipNoteStyle() {
            return { marginLeft: 'auto', fontSize: '11px' };
        },
        cardSectionLabelStyle() {
            return {
                fontSize: '12px', color: C_INK2, fontWeight: 600,
                marginBottom: '10px', letterSpacing: '0.1px',
            };
        },
        typeRowsStyle() {
            return { display: 'flex', flexDirection: 'column', gap: '10px' };
        },
        typeRowHeadStyle() {
            return {
                display: 'flex', alignItems: 'center', gap: '12px',
                cursor: 'pointer',
            };
        },
        typeRowBodyStyle() {
            return { flex: 1 };
        },
        typeRowTitleStyle() {
            return {
                fontFamily: FONT_DISPLAY, fontSize: '15px', fontWeight: 700,
                letterSpacing: '-0.2px',
            };
        },
        typeRowSubStyle() {
            return { fontSize: '12px', color: C_MUTED, marginTop: '1px' };
        },
        typeRowExpandStyle() {
            return { marginTop: '14px', paddingLeft: '34px' };
        },
        typeRowExpandGridStyle() {
            return {
                display: 'grid',
                gridTemplateColumns: this.isMobile ? '1fr' : '1fr 1fr',
                gap: this.isMobile ? '10px' : '14px',
            };
        },
        typeRowExpandGrid2Style() {
            return {
                display: 'grid',
                gridTemplateColumns: this.isMobile ? '1fr' : '1fr 1fr',
                gap: this.isMobile ? '10px' : '14px',
            };
        },
        typeRowExpandOneStyle() {
            return { maxWidth: '280px' };
        },
        feeModeGridStyle() {
            return {
                display: 'grid',
                gridTemplateColumns: this.isMobile ? '1fr' : '1fr 1fr',
                gap: '8px',
            };
        },
        feeModeLabelStyle() {
            return { fontSize: '13px', fontWeight: 600 };
        },
        frequencyWrapStyle() {
            return { gridColumn: '1 / -1', maxWidth: '240px' };
        },
        cRadioInnerStyle() {
            return {
                width: '11px', height: '11px', borderRadius: '50%',
                background: C_INK, display: 'inline-block',
            };
        },
        invoicedHeaderStyle() {
            return {
                display: 'flex', alignItems: 'center',
                justifyContent: 'space-between', gap: '14px',
            };
        },
        invoicedToggleStyle() {
            return {
                display: 'flex', alignItems: 'center', gap: '12px',
                cursor: 'pointer',
            };
        },
        invoicedTitleStyle() {
            return {
                fontFamily: FONT_DISPLAY, fontSize: '17px', fontWeight: 700,
                letterSpacing: '-0.2px',
            };
        },
        invoicedPeriodStyle() {
            return {
                marginLeft: '8px', fontSize: '12px', color: C_MUTED, fontWeight: 500,
            };
        },
        invoicedActiveBadgeStyle() {
            return {
                fontSize: '11px', color: C_GREEN, fontWeight: 600,
                letterSpacing: '0.5px', textTransform: 'uppercase',
            };
        },
        invoiceGridStyle() {
            // Mobile: collapse 4-col grid to 2 cols — gives Invoice # /
            // Amount / Collected / Hard cost two rows of two on phones.
            return {
                marginTop: '18px',
                display: 'grid',
                gridTemplateColumns: this.isMobile ? '1fr 1fr' : '1fr 1fr 1fr 1fr',
                gap: this.isMobile ? '10px' : '12px',
            };
        },
        termsWrapStyle() {
            return { marginTop: '14px' };
        },
        textareaStyle() {
            return {
                width: '100%', minHeight: '88px', padding: '14px',
                border: `1.5px solid ${C_HAIR}`, borderRadius: '14px',
                background: C_CREAM, resize: 'vertical',
                fontFamily: FONT_BODY, fontSize: '14px',
                color: C_INK, outline: 'none', lineHeight: 1.55,
            };
        },
        fieldErrorStyle() {
            return {
                display: 'block', marginTop: '6px',
                fontSize: '12px', color: C_RED, fontWeight: 500,
            };
        },
        previewCardStyle() {
            return { padding: '20px' };
        },
        previewLabelStyle() {
            return {
                fontSize: '11px', color: C_MUTED, letterSpacing: '0.5px',
                textTransform: 'uppercase', fontWeight: 700, marginBottom: '10px',
            };
        },
        previewTitleStyle() {
            return {
                fontFamily: FONT_DISPLAY, fontSize: '17px', fontWeight: 700,
                letterSpacing: '-0.3px', lineHeight: 1.2,
            };
        },
        previewSubStyle() {
            return { fontSize: '12px', color: C_MUTED, marginTop: '4px' };
        },
        previewKvBlockStyle() {
            return {
                marginTop: '14px', paddingTop: '14px',
                borderTop: `1px solid ${C_HAIR}`,
                display: 'flex', flexDirection: 'column', gap: '7px',
                fontSize: '13px',
            };
        },
        previewKvRowStyle() {
            return { display: 'flex', justifyContent: 'space-between', gap: '12px' };
        },
        previewKvKStyle() {
            return { color: C_MUTED };
        },
        previewKvVStyle() {
            return { color: C_INK, fontWeight: 600 };
        },
        previewInvoiceCardStyle() {
            return {
                marginTop: '14px', padding: '12px 14px',
                background: C_INK, color: '#fff', borderRadius: '12px',
            };
        },
        previewInvoiceLabelStyle() {
            return {
                fontSize: '10px', color: '#FFFFFF99', letterSpacing: '0.5px',
                textTransform: 'uppercase', fontWeight: 700,
            };
        },
        previewInvoiceValueStyle() {
            return {
                fontFamily: FONT_DISPLAY, fontSize: '22px', fontWeight: 700,
                letterSpacing: '-0.6px', marginTop: '4px',
            };
        },
        previewInvoiceBreakdownStyle() {
            return {
                fontSize: '11px', color: '#FFFFFFB0', marginTop: '2px',
            };
        },
        rightHintStyle() {
            return {
                padding: '14px 16px',
                display: 'flex', gap: '10px', alignItems: 'flex-start',
                fontSize: '12px', color: C_MUTED, lineHeight: 1.55,
            };
        },
        rightHintIconStyle() {
            return {
                flexShrink: 0, width: '18px', height: '18px', borderRadius: '50%',
                background: '#fff', border: `1px solid ${C_HAIR}`, color: C_INK2,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '11px', fontWeight: 700,
            };
        },
        headerStyle() {
            return { marginBottom: '24px', padding: '0 8px' };
        },
        eyebrowStyle() {
            return {
                fontSize: '12px', color: C_MUTED, letterSpacing: '0.6px',
                textTransform: 'uppercase', fontWeight: 600,
            };
        },
        h1Style() {
            return {
                margin: '6px 0 8px', fontFamily: FONT_DISPLAY,
                fontSize: '34px', fontWeight: 700, letterSpacing: '-1px',
                lineHeight: 1.05, color: C_INK,
            };
        },
        dealAccentStyle() {
            return { color: C_RED };
        },
        leadStyle() {
            return { margin: 0, fontSize: '15px', color: C_MUTED, lineHeight: 1.55 };
        },
        cardStyle() {
            return { marginBottom: '18px', padding: '32px 36px' };
        },
        navStyle() {
            return navMobileStickyStyle(this.isMobile, {
                marginTop: '8px', padding: '16px 24px',
                background: '#fff', borderRadius: '16px',
                border: `1px solid ${C_HAIR}`,
                display: 'flex', alignItems: 'center',
                justifyContent: 'space-between', gap: '16px',
            });
        },
        navHintStyle() {
            return { fontSize: '12px', color: C_MUTED, fontStyle: 'italic' };
        },
        loaderStyle() {
            return {
                display: 'flex', alignItems: 'center',
                justifyContent: 'center', padding: '32px',
            };
        },
    },
    methods: {
        // ===== c-design helpers =====
        cRadioStyle(checked) {
            return {
                display: 'flex', alignItems: 'center', gap: '10px',
                padding: '10px 12px',
                background: checked ? C_CREAM : '#fff',
                border: `1.5px solid ${checked ? C_INK : C_HAIR}`,
                borderRadius: '12px', cursor: 'pointer',
                position: 'relative',
            };
        },
        cRadioDotStyle(checked) {
            return {
                width: '22px', height: '22px', borderRadius: '50%',
                border: `1.5px solid ${checked ? C_INK : C_HAIR}`,
                background: '#fff', flexShrink: 0,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            };
        },
        typeRowStyle(checked) {
            return {
                border: `1.5px solid ${checked ? C_INK : C_HAIR}`,
                background: checked ? C_CREAM : '#fff',
                borderRadius: '14px', padding: '14px 16px',
                transition: 'all 120ms',
            };
        },
        typeRowSub(typeId) {
            return {
                1: 'Recurring monthly billing',
                2: 'Fixed total billed over N payments',
                3: 'A single charge, no recurrence',
            }[typeId] || '';
        },
        customToggleBoxStyle(checked) {
            return {
                width: '20px', height: '20px', borderRadius: '6px',
                border: `1.5px solid ${checked ? C_INK : C_HAIR}`,
                background: checked ? C_INK : '#fff',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', fontSize: '11px', fontWeight: 700, flexShrink: 0,
            };
        },

        async getData() {
            await axios
                .post("/api/wizard/getList", {
                    page: "new_upsell",
                    deal_id: this.getDealId,
                    sow_index: this.sowIndex,
                    upsell_index: this.upsellIndex,
                })
                .then((res) => {
                    this.services_list = res.data.page.services;
                    this.wizard_draft_sow_id =
                        res.data.page.wizard_draft_sow_id;
                    this.upsell.sow_title = res.data.page.sow_title;

                    if (res.data.page.upsell_draft) {
                        this.setDataFromDraft(res.data.page.upsell_draft);
                    }
                })
                .catch((err) => console.log("err getData upsell", err));
        },
        initDatePicker() {
            const node__datepicker = this.$refs.datepicker;

            if (!node__datepicker) {
                return;
            }

            let datepicker = null;
            const input = node__datepicker.querySelector("input");
            const button = node__datepicker.querySelector("button");

            if (input) {
                datepicker = new Datepicker(input, this.datepicker_options);
            }

            if (button) {
                button.addEventListener("click", () => {
                    if (!datepicker.active) {
                        datepicker.show();
                    }
                });
            }
        },

        setDataFromDraft(data = null) {
            if (data) {
                this.upsell.services = data.service_id;
                this.upsell.kick_off_date = data.kickoff_date;
                this.upsell.custom_service = data.custom_service;
                if (data.custom_service) {
                    this.is_create_custom_service = true;
                }
                this.upsell.type = data.type_id;
                this.upsell.number_of_pmts = data.num_of_payments;
                this.upsell.terms = data.terms;
                this.upsell.mounthly_fee =
                    data.type_id === 1 ? data.amount : "";
                this.upsell.full_amount = data.type_id === 2 ? data.amount : "";
                this.upsell.one_time_fee =
                    data.type_id === 3 ? data.amount : "";
                this.upsell.is_fluctuating_fee = data.fluctuating_fee;
                this.upsell.frequency = data.frequency ? data.frequency : 1;
                this.isHasInvoice = data.new_invoice.is_exist || false;

                if (data.new_invoice.is_exist) {
                    this.newInvoice.amount = data.new_invoice.amount;
                    this.newInvoice.collected = data.new_invoice.collected;
                    this.newInvoice.hard_cost = data.new_invoice.hard_cost;
                    this.newInvoice.invoice_number =
                        data.new_invoice.invoice_number;
                }
            }
        },

        onChangeDatePicker(e) {
            const date = e.target.value;

            if (date) {
                this.upsell.kick_off_date = date;
            }
        },

        handleChangeAmount(e) {
            const value = e.target.value;
            if (value) {
                switch (this.upsell.type) {
                    case 1:
                        this.upsell.mounthly_fee = value;
                        break;
                    case 2:
                        this.upsell.full_amount = value;
                        break;
                    case 3:
                        this.upsell.one_time_fee = value;
                        break;
                }
            }
        },
        getAmountLabel(id) {
            switch (id) {
                case 1:
                    return "Monthly fee";
                case 2:
                    return "Full Amount";
                case 3:
                    return "One-time fee";
                default:
                    return "Monthly fee";
            }
        },
        get_deal_service_type(id = null) {
            if (!id) {
                return;
            }

            switch (id) {
                case 1:
                    return "mounthly_fee";
                case 2:
                    return "full_amount";
                case 3:
                    return "one_time_fee";
                default:
                    return;
            }
        },
        onChangeType() {
            switch (this.upsell.type) {
                case 1:
                    this.upsell.full_amount = 0;
                    this.upsell.one_time_fee = 0;
                    break;
                case 2:
                    this.upsell.one_time_fee = 0;
                    this.upsell.mounthly_fee = 0;
                    break;
                case 3:
                    this.upsell.mounthly_fee = 0;
                    this.upsell.full_amount = 0;
                    break;
            }
        },

        onNextPage() {
            if (this.isEditUpsell) {
                this.onChangeSection(SECTIONS.FINAL_DASHBOARD, SECTIONS.UPSELL);
                return;
            }
            this.onChangeSection(SECTIONS.DO_YOU_HAVE_SERVICE, SECTIONS.UPSELL);
        },

        onPrevSection() {
            if (this.isEditUpsell) {
                this.onChangeSection(
                    SECTIONS.DO_YOU_HAVE_SERVICE_WITHOUT_SOW,
                    SECTIONS.DO_YOU_HAVE_UPSELL
                );
                return;
            }
            this.$store.commit("setIndexSOW", Number(this.sowIndex) - 1);
            this.onChangeSection(
                SECTIONS.DO_YOU_HAVE_SERVICE_WITHOUT_SOW,
                SECTIONS.DO_YOU_HAVE_UPSELL
            );
        },

        onSaveNewUpsell() {
            this.validateFields();

            if (
                this.errors.amount ||
                this.errors.service_id ||
                this.errors.type ||
                this.errors.sow_file ||
                this.errors.kick_off_date ||
                (this.errors.number_of_pmts && this.upsell.type === 2) ||
                this.errors.custom_service ||
                this.errors.is_fluctuating_fee
            ) {
                return;
            }

            this.isLoading = true;

            const data = {
                deal_id: this.getDealId,
                service_id: this.upsell.services,
                kickoff_date: this.upsell.kick_off_date,
                custom_service: this.upsell.custom_service,
                //amount: this.upsell.full_amount,
                //mounthly_fee: this.upsell.mounthly_fee,
                type_id: this.upsell.type,
                num_of_payments: this.upsell.number_of_pmts,
                //one_time_setup_fee: this.upsell.one_time_fee,
                terms: this.upsell.terms,
                sow_id: this.upsell.sow_file,
                amount:
                    this.upsell.type === 1
                        ? this.upsell.mounthly_fee
                        : this.upsell.type === 2
                        ? this.upsell.full_amount
                        : this.upsell.one_time_fee,
                fluctuating_fee: this.upsell.is_fluctuating_fee,
                wizard_draft_sow_id: this.wizard_draft_sow_id,
                upsell_index: this.upsellIndex,
                sow_index: this.sowIndex,
                frequency_retainer: this.upsell.frequency,
                new_invoice: {
                    is_exist: this.isHasInvoice,
                    invoice_number: this.newInvoice.invoice_number,
                    amount: this.newInvoice.amount,
                    hard_cost: this.newInvoice.hard_cost,
                    collected: this.newInvoice.collected,
                },
            };

            axios
                .post("/api/wizard/updateDraft", {
                    action: "createDealLineItem",
                    data: data,
                })
                .then(() => {
                    //this.$toast.success("New upsell has been saved!");
                    this.onNextPage();
                    this.$store.commit("setIsWithoutSOW", false);
                })
                .catch((err) => {
                    this.$toast.error("Something went wrong!");
                    console.error(err);
                })
                .finally(() => (this.isLoading = false));
        },
        validateFields() {
            this.errors.service_id = null;
            this.errors.type = null;
            this.errors.amount = null;
            this.errors.number_of_pmts = null;
            this.errors.sow_file = null;
            this.errors.custom_service = null;
            this.errors.kick_off_date = null;
            this.errors.is_fluctuating_fee = null;

            if (!this.upsell.services.length && !this.upsell.custom_service) {
                this.errors.custom_service = "Choose a service or enter custom";
            }
            if (!this.upsell.kick_off_date) {
                this.errors.kick_off_date = "Choose kick off date";
            }

            if (!this.upsell.type) {
                this.errors.type = "Required";
            }

            if (
                this.upsell.type === 1 &&
                this.upsell.is_fluctuating_fee === null
            ) {
                this.errors.is_fluctuating_fee = "Required";
            }
            if (
                (this.upsell.type === 2 && !this.upsell.full_amount) ||
                (this.upsell.full_amount &&
                    (this.upsell.full_amount <= 0 ||
                        !/^\d+$/.test(this.upsell.full_amount)))
            ) {
                this.errors.amount = "Invalid value";
            }

            if (this.upsell.type === 2 && !this.upsell.number_of_pmts) {
                this.errors.number_of_pmts = "Required";
            }

            if (
                this.upsell.type === 1 &&
                this.upsell.is_fluctuating_fee === false &&
                (this.upsell.mounthly_fee <= 0 ||
                    !/^\d+$/.test(this.upsell.mounthly_fee))
            ) {
                this.errors.amount = "Invalid value";
            }
            if (
                this.upsell.type === 3 &&
                (this.upsell.one_time_fee <= 0 ||
                    !/^\d+$/.test(this.upsell.one_time_fee))
            ) {
                this.errors.amount = "Invalid value";
            }

            if (
                this.upsell.number_of_pmts &&
                (this.upsell.number_of_pmts < 0 ||
                    !/^\d+$/.test(this.upsell.number_of_pmts))
            ) {
                this.errors.number_of_pmts = "Invalid value";
            }
        },

        handleChangeInoviced(is_checked) {
            if (is_checked) {
                setTimeout(() => reInitAllTooltips(), 750);
            }

            this.isHasInvoice = is_checked;
        },

        onChangeFluctuatingFee(flag = null) {
            this.upsell.is_fluctuating_fee = flag;
        },
    },
};
</script>
